import { PurchasesTracesDialogComponent } from '../components/accounting/purchases-traces/purchases-traces-dialog/purchases-traces-dialog.component';
import { PurchasesTracesStatistics } from '../models/accounting/purchases-traces-statistics.model';
import { PurchaseTrace } from '../models/accounting/purchase-trace.model';
import { BaseComponentService } from './_app/base-component.service';
import { ApiPagination } from '../models/api/api-pagination.model';
import { ApiResponse } from '../models/api/api-response.model';
import { ApiError } from '../models/api/api-error.model';
import { BehaviorSubject, catchError, map, Observable, throwError } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppTools } from '../utils/tools';
import { API } from '../constants/api';

@Injectable({ providedIn: 'root' })
export class PurchasesTracesService {
  private _baseComponentService = inject(BaseComponentService);
  private _http: HttpClient = inject(HttpClient);

  private _categories: { label: string; color: string }[] = [];

  get categories(): { label: string; color: string }[] {
    return this._categories;
  }

  get categoriesLabelsOptions(): { value: string; label: string }[] {
    return this.categories.map((category) => {
      let label = AppTools.capitalizeFirstLetterOfEachWord(
        category.label.toLowerCase()
      );
      if (label.toUpperCase() == 'IFTS') label = 'IFTS';
      return { value: category.label.trim(), label: label };
    });
  }

  categoriesOptions$ = new BehaviorSubject<{ value: string; label: string }[]>(
    []
  );

  public schoolYears$ = new BehaviorSubject<string[]>([]);

  getCategoryColor(label: string): string {
    const obj = this._categories.filter(
      (o) => o.label.toUpperCase().trim() === label.toUpperCase().trim()
    );
    return obj.at(0)?.color || '';
  }

  constructor() {
    if (this.categories.length === 0) {
      this.getPurchaseTraceCategories().subscribe();
    }

    if (this.schoolYears$.value.length === 0) {
      this.getPurchaseTraceSchoolYears().subscribe();
    }
  }

  getPurchaseTraceCategories() {
    return this._http
      .get<ApiResponse<{ full: { label: string; color: string }[] }>>(
        API.purchaseTraceCategoriesConfigs(),
        { withCredentials: true }
      )
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          this._categories = response.data?.full || [];
          this.categoriesOptions$.next(this.categoriesLabelsOptions);
          return response;
        })
      );
  }

  getPurchaseTraceSchoolYears() {
    return this._http
      .get<ApiResponse<string[]>>(API.purchaseTraceSchoolYearsConfigs(), {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          this.schoolYears$.next(response.data || []);
          return response;
        })
      );
  }

  getPurchasesTracesListing(params: any) {
    return this._http
      .get<ApiResponse<PurchaseTrace[]>>(API.purchasesTracesListing(), {
        withCredentials: true,
        params,
      })
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          response.pagination = new ApiPagination(response.pagination);
          response.data = (response.data ?? []).map(
            (json) => new PurchaseTrace(json)
          );
          return response;
        })
      );
  }

  getPurchaseTraceDetails(id?: string) {
    return this._http
      .get<ApiResponse<PurchaseTrace>>(API.purchaseTraceDetails(id), {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          response.data = new PurchaseTrace(response.data);
          return response;
        })
      );
  }

  postPurchaseTrace(data: any) {
    return this._http
      .post<ApiResponse<PurchaseTrace>>(API.purchaseTrace(), data, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          response.data = new PurchaseTrace(response.data);
          return response;
        })
      );
  }

  patchPurchaseTrace(id: string, data: any) {
    return this._http
      .patch<ApiResponse<PurchaseTrace>>(API.purchaseTraceDetails(id), data, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          response.data = new PurchaseTrace(response.data);
          return response;
        })
      );
  }

  deletePurchaseTraceAttachment(pt?: PurchaseTrace) {
    return this._http
      .delete<ApiResponse<any>>(
        API.purchaseTraceAttachment(pt?.id, pt?.attachment),
        { withCredentials: true }
      )
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          return response;
        })
      );
  }

  deletePurchaseTrace(id?: string) {
    return this._http
      .delete<ApiResponse<any>>(API.purchaseTraceDetails(id), {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          return response;
        })
      );
  }

  getPurchasesTracesStatistics(params: any) {
    return this._http
      .get<ApiResponse<PurchasesTracesStatistics[]>>(
        API.purchasesTracesStatistics(),
        {
          withCredentials: true,
          params,
        }
      )
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          response.data = (response.data ?? []).map(
            (json) => new PurchasesTracesStatistics(json)
          );
          return response;
        })
      );
  }

  // Dialog
  openPurchaseTraceDialog(data?: any) {
    const dialogSize = '650px';
    return this._baseComponentService.dialog.open(
      PurchasesTracesDialogComponent,
      {
        maxWidth: dialogSize,
        minWidth: dialogSize,
        restoreFocus: false,
        autoFocus: false,
        data,
      }
    );
  }
}
